import React from 'react';
import { Link } from 'react-router-dom';
import { AppState, CartState, CartItemsState } from 'state';
import { getDateFormat, getCurrencyFormat } from 'ui/i18n/formats';
import { formatMoney } from 'accounting';
import { authorize } from 'utils/auth';
import { getIndexForItem } from 'state/helpers';
import { FormattedMessage } from 'ui/i18n';

import {
	SoloItemPackage,
	SoloItem,
	RemoveItem,
	DropShipAddress,
	EditableNumber,
	CartItemName,
	InvoiceHoldWeeks,
	EstShipDate,
} from 'ui/components';

/**
 * Renders a moment instance using i18n
 *
 * @param {Moment} value - the moment instance to be rendered
 * @param {String} instead - what to render in case the date is invalid
 * @param {String} formatName - format name from locale/formats
 * @return {Component|String} FormattedDate or date string
 */
export function render_date(value, instead = null, formatName = 'standard') {
	const format = getDateFormat(
		AppState.user.country.short_name,
		AppState.locale
	)[formatName];
	return value.isValid && value.isValid() ? value.format(format) : instead;
}

/**
 * Render a number formatted as an amount based on user countryName
 *
 * @param {Number} value Amount to be formatted and rendered
 * @param {Object} item The item that contains the amount to be rendered
 * @returns {String} Formatted amount
 */
export function render_amount(value = 0, item = '') {
	const currency = item.currency ? item.currency : '';
	const { country } = AppState.user;
	const symbol = currency.currency_symbol
		? currency.currency_symbol
		: country.currency.symbol;
	const countryShortName = currency.country_short_name
		? currency.country_short_name
		: country.short_name;

	const opts = getCurrencyFormat(countryShortName, AppState.locale);

	return formatMoney(value, Object.assign({ symbol }, opts));
}

export function render_bad_lines(value) {
	return value
		? <p className="processing-error">
				{value}
			</p>
		: value;
}

/**
 * Render value through FormattedMessage for i18n
 *
 * @param {String} value Formatted message partial id (yes/no)
 * @returns {Component} FormattedMessage
 */
export function render_yes_no(value) {
	return <FormattedMessage id={`app.${value}`} defaultValue={value} />;
}

/**
 * Renders a link based on the order number
 *
 * @param {String} poNumber Order number
 * @returns {Component} Link to order details
 */
export function render_po_number(poNumber) {
	return (
		<Link to={`/order-history/${poNumber}`}>
			{poNumber}
		</Link>
	);
}

/**
 * Render an solo item pack
 *
 * @param {String} itemId Item id that will be rendered
 * @param {Object} item The item that contains the data needed for rendering
 * @returns {String} Rendered solo item pack
 */
export function render_solo_item_package(itemId, item) {
	return (
		<SoloItemPackage
			imageUrl={item.image_url}
			itemName={item.name}
			itemNumber={item.number}
			itemBaseNumber={item.base_number}
			isConfigurable={item.is_configurable}
		/>
	);
}

/**
 * Render an solo item
 *
 * @param {String} itemId Item id that will be rendered
 * @param {Object} item The item that contains the data needed for rendering
 * @param {Function} toggleModal The toggleModal callback
 * @param {Boolean} nameIsRequired Display name also if true
 * @param {Boolean} hover Display pack items list on hover, if true
 *  @param {Boolean} baseNumberRequired Display base number, if true
 * @returns {String} An solo item link with params
 */
export function render_solo_item(
	itemId,
	item,
	toggleModal,
	nameIsRequired,
	hover,
	baseNumberRequired
) {
	return (
		<SoloItem
			soloItem={item}
			toggleModal={toggleModal}
			nameIsRequired={nameIsRequired}
			baseNumberRequired={baseNumberRequired}
			hover={hover}
		/>
	);
}

/**
 * Render an cart item
 *
 * @param {Object} item The item that contains the data needed for rendering
 * @param {Function} toggleModal The toggleModal callback
 * @returns {String} An cart item link with params
 */
export function render_cart_item(item, toggleModal) {
	return <CartItemName item={item} toggleModal={toggleModal} />;
}

/**
 * Render component if current user is from one of the countries
 *
 * @param {Array} countries The accepted countries to render the component
 * @param {Object} component The component to be rendered
 * @returns {Object|null} The component or null
 */
export function render_4country(countries, component) {
	const countryName = AppState.user.country.short_name;

	if (countries.includes(countryName)) {
		return component;
	}

	return null;
}

/**
 * Renders the number of hold invoice weeks or
 * localized see components message
 *
 * @param {Object} item The item containing the number of hold invoice weeks
 * @param {String} value The number of hold invoice weeks
 * @return {String|Component} The number or localized message
 */
export function render_holdInvWeeks(item, value) {
	return item.is_pack
		? <FormattedMessage
				id="item.See_Components"
				defaultMessage={value.toString()}
			/>
		: value;
}

/**
 * Renders the editable number of hold invoice weeks or
 * the number
 *
 * @param {Object} item The object containing the number of hold invoice weeks
 * @return {String|Component} The number or localized message
 */
export function render_editable_holdInvWeeks(item) {
	return <InvoiceHoldWeeks item={item} />;
}

/**
 * Renders the est ship date weeks
 *
 * @param {Object} item The object containing the est ship date
 * @return {String|Component} The number or localized message
 */
export function render_est_ship_date(item) {
	return <EstShipDate item={item} />;
}

/**
 * Renders the remove button
 *
 * @param {Object} item The current item
 * @param {Array} items The items we are rendering
 * @param {Function} removeHandler The handler for remove action
 * @return {Component} The remove button
 */
export function render_remove(item, items, removeHandler) {
	const index = getIndexForItem(items, item, item.is_pack);

	return (
		<RemoveItem
			remove={removeHandler}
			index={index}
			item={item}
			requieresText={false}
		/>
	);
}

/**
 * Renders the value when it must be an input
 *
 * @param {Number} item The item containing the details
 * @param {Number} value The quantity
 * @param {Boolean} isFromCart True if renders is called from cart page
 * @param {Function} onChangeHandler The handler for quantity change
 * @param {Function} onBlurHandler The handler for blur event
 * @return {Component} The quantity input
 */
export function render_editable_number(
	item,
	value,
	isFromCart,
	onChangeHandler,
	onBlurHandler
) {
	const isDisabled =
		CartItemsState.isSoldOut(item) ||
		AppState.isOnHoldOrTerminated ||
		item.is_preview ||
		!authorize(['AddToCart'], true, false);

	const errorMsgId = CartItemsState.getErrorMessageId(
		isFromCart,
		CartState.getAvailableQuantity(item, isFromCart),
		CartState.getTotalQuantityFromCart(item),
		item
	);

	const availableQuantity = CartState.getAvailableQuantity(item, isFromCart);
	const cartQuantity = CartState.getCartQuantity(item);

	return (
		<EditableNumber
			item={item}
			value={value}
			isDisabled={isDisabled}
			errorMsgId={errorMsgId}
			availableQuantity={availableQuantity}
			cartQuantity={cartQuantity}
			onChangeHandler={onChangeHandler}
			onBlurHandler={onBlurHandler}
		/>
	);
}

/**
 * Renders the drop ship address
 *
 * @param {Array} item The item containing the address
 * @return {Component} A dropdown with 4 options: address, new address, edit address, none
 */
export function render_drop_ship_address(item) {
	if (item.is_drop_ship) {
		return <DropShipAddress item={item} />;
	}
	return 'N/A';
}

/**
 * Renders the quantity
 *
 * @param {number} value The quantity of the product
 *
 * @return {string|number} The displayed quantity of the product
 */
export function render_quantity(value) {
	return value === 0 ? <FormattedMessage id="item.soldOut" /> : value;
}



// WEBPACK FOOTER //
// ./src/utils/render.js